import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Plus, X } from "lucide-react";
import { useSheetHistory } from "@/hooks/useSheetHistory";

const slugify = (token) => token.toLowerCase().replace(/[^a-z0-9]+/g, "-");

/**
 * The allergy picker's grammar for any list of tags: a chip field that opens a
 * bottom sheet with search, tickable options, and — unlike allergies — a row
 * to add exactly what was typed. Favorites and excluded ingredients use it, so
 * "tap to select" behaves the same wherever a profile list is edited.
 *
 * `values` is a plain array of strings. Anything in it that matches no option
 * is a custom token: it stays a removable chip and a ticked row, because a
 * dish from another restaurant or an ingredient off the list is still the
 * guest's truth even though no catalog produced it.
 */
const TagPicker = ({
  label,
  values,
  onChange,
  options,
  testIdPrefix,
  searchPlaceholder,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const lowerValues = values.map((v) => v.toLowerCase());
  const optionByLower = new Map(options.map((o) => [o.value.toLowerCase(), o]));
  const customTokens = values.filter((v) => !optionByLower.has(v.toLowerCase()));

  const toggle = (value) => {
    const lower = value.toLowerCase();
    if (lowerValues.includes(lower)) {
      onChange(values.filter((v) => v.toLowerCase() !== lower));
    } else {
      onChange([...values, value]);
    }
  };

  const close = () => {
    setOpen(false);
    setSearch("");
  };
  useSheetHistory(open, close);

  const query = search.trim().toLowerCase();
  const filtered = query
    ? options.filter(
        (o) => o.label.toLowerCase().includes(query) || o.value.toLowerCase().includes(query)
      )
    : options;
  // Offer to add the typed text verbatim when it names nothing we already show.
  const canAddCustom =
    query.length > 0 && !optionByLower.has(query) && !lowerValues.includes(query);

  const chips = [
    ...options
      .filter((o) => lowerValues.includes(o.value.toLowerCase()))
      .map((o) => ({ testid: `${testIdPrefix}-chip-${o.key}`, label: o.label, value: o.value })),
    ...customTokens.map((token) => ({
      testid: `${testIdPrefix}-chip-${slugify(token)}`,
      label: token,
      value: token,
    })),
  ];

  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5 block px-1">
        {label}
      </label>
      <div
        role="button"
        tabIndex={0}
        data-testid={`${testIdPrefix}-trigger`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter") {
            setOpen(true);
          } else if (e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="flex w-full min-h-14 items-center bg-white border border-neutral-200 rounded-2xl px-4 py-2 text-left"
      >
        {chips.length > 0 ? (
          <div className="flex w-full flex-wrap items-center gap-1.5">
            {chips.map((chip) => (
              <span
                key={chip.testid}
                data-testid={chip.testid}
                className="rounded-full bg-neutral-100 h-8 px-3 inline-flex items-center gap-1 text-xs font-semibold text-neutral-800"
              >
                {chip.label}
                <button
                  type="button"
                  aria-label={t("common.remove")}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(chip.value);
                  }}
                  className="shrink-0"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-base font-medium text-neutral-400">{t("tagPicker.placeholder")}</span>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center"
          onClick={close}
        >
          <div
            data-testid={`${testIdPrefix}-sheet`}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            className="w-full sm:max-w-md max-h-[80vh] bg-white rounded-t-3xl sm:rounded-3xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 px-4 pt-4 pb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 px-1 py-1">
                {label}
              </div>
              <input
                data-testid={`${testIdPrefix}-search`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="mt-1 w-full h-11 bg-neutral-100 rounded-xl px-4 text-sm font-medium text-neutral-900 placeholder:text-neutral-400 outline-none"
              />
            </div>

            <div className="flex-1 overflow-y-auto px-2 pb-2">
              {canAddCustom && (
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-add-custom`}
                  onClick={() => {
                    toggle(search.trim());
                    setSearch("");
                  }}
                  className="w-full min-h-12 flex items-center gap-2 px-2 rounded-xl text-left text-base font-semibold text-[#E34A27] active:bg-neutral-50 transition-colors"
                >
                  <Plus size={16} className="shrink-0" />
                  {t("tagPicker.addCustom", { text: search.trim() })}
                </button>
              )}
              {filtered.length === 0 && !canAddCustom ? (
                <div className="px-4 py-6 text-center text-sm text-neutral-400">
                  {t("tagPicker.empty")}
                </div>
              ) : (
                filtered.map((o) => {
                  const checked = lowerValues.includes(o.value.toLowerCase());
                  return (
                    <button
                      key={o.key}
                      type="button"
                      data-testid={`${testIdPrefix}-option-${o.key}`}
                      aria-pressed={checked}
                      onClick={() => toggle(o.value)}
                      className="w-full min-h-12 flex items-center justify-between gap-3 px-2 rounded-xl text-left text-base font-semibold text-neutral-900 active:bg-neutral-50 transition-colors"
                    >
                      {o.label}
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded ${
                          checked ? "bg-neutral-900 text-white" : "border border-neutral-300"
                        }`}
                      >
                        {checked && <Check size={14} />}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <div className="shrink-0 p-4 pt-2">
              <button
                type="button"
                data-testid={`${testIdPrefix}-done`}
                onClick={close}
                className="w-full h-12 rounded-2xl bg-neutral-900 text-white font-bold active:scale-[0.98] transition-transform"
              >
                {t("tagPicker.done")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagPicker;
